import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Container, Box, Typography, Card, CardContent, Chip, Divider,
  CircularProgress, Avatar, Paper, Stack
} from '@mui/material';
import {
  Schedule as ScheduleIcon, Store as StoreIcon,
  Person as PersonIcon, CheckCircle as CheckIcon,
  RadioButtonChecked as CurrentIcon, RadioButtonUnchecked as PendingIcon,
  ErrorOutline as ErrorIcon
} from '@mui/icons-material';
import config from '../config';

const statusColors = {
  1: '#FB8C00', 2: '#1E88E5', 3: '#7B1FA2', 4: '#43A047',
  5: '#2E7D32', 6: '#FFA000', 7: '#C62828', 8: '#4CAF50',
  9: '#0891B2', 10: '#059669',
};

function formatDate(d) {
  if (!d) return '';
  const dt = new Date(d);
  return `${String(dt.getDate()).padStart(2, '0')}/${String(dt.getMonth() + 1).padStart(2, '0')}/${dt.getFullYear()} ${String(dt.getHours()).padStart(2, '0')}:${String(dt.getMinutes()).padStart(2, '0')}`;
}

export default function SeguimientoPublico() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await fetch(`${config.API_BASE_URL}/api/v1/tracking-links/ver/${token}`);
        const json = await res.json();
        if (!res.ok || !json.success) {
          setError(json.mensaje || 'Enlace no válido');
        } else {
          setData(json.data);
        }
      } catch {
        setError('Error de conexión');
      } finally {
        setLoading(false);
      }
    };
    if (token) fetchData();
    else { setError('Token no proporcionado'); setLoading(false); }
  }, [token]);

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#F8FAFC' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#F8FAFC' }}>
        <Card sx={{ maxWidth: 400, textAlign: 'center', borderRadius: 3, p: 4 }}>
          <ErrorIcon sx={{ fontSize: 60, color: '#EF4444', mb: 2 }} />
          <Typography variant="h5" fontWeight={700} color="#EF4444" gutterBottom>Enlace no disponible</Typography>
          <Typography color="text.secondary">{error}</Typography>
        </Card>
      </Box>
    );
  }

  const { orden, detalles, timeline, trabajador, link_expira } = data;
  const color = statusColors[orden?.id_estado_servicio] || '#999';

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#F8FAFC' }}>
      {/* Header */}
      <Box sx={{ bgcolor: '#1E2783', color: '#fff', py: 2.5, textAlign: 'center' }}>
        <Typography variant="h6" fontWeight={800}>{orden?.nombre_empresa || 'TRACKEA'}</Typography>
        <Typography variant="caption" sx={{ opacity: 0.8 }}>Seguimiento de Servicio</Typography>
      </Box>

      <Container maxWidth="sm" sx={{ py: 3 }}>
        {/* Estado actual */}
        <Card sx={{ borderRadius: 3, mb: 2, border: `2px solid ${color}20` }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" fontWeight={800} color="#1E2783">
                Servicio #{orden?.id_orden_servicio}
              </Typography>
              <Chip
                label={orden?.descripcion_estado || 'Sin estado'}
                sx={{ bgcolor: color, color: '#fff', fontWeight: 700 }}
              />
            </Box>
            <Divider sx={{ mb: 2 }} />

            <Stack spacing={1.5}>
              {orden?.fecha_orden && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <ScheduleIcon sx={{ color: '#94A3B8', fontSize: 20 }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary">Fecha</Typography>
                    <Typography variant="body2" fontWeight={600}>{formatDate(orden.fecha_orden)}</Typography>
                  </Box>
                </Box>
              )}
              {orden?.nombre_local && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <StoreIcon sx={{ color: '#94A3B8', fontSize: 20 }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary">Local</Typography>
                    <Typography variant="body2" fontWeight={600}>{orden.nombre_local}</Typography>
                    {orden.direccion_local && (
                      <Typography variant="caption" color="text.secondary">{orden.direccion_local}</Typography>
                    )}
                  </Box>
                </Box>
              )}
              {orden?.tipo_servicio && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <PersonIcon sx={{ color: '#94A3B8', fontSize: 20 }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary">Tipo de servicio</Typography>
                    <Typography variant="body2" fontWeight={600}>{orden.tipo_servicio}</Typography>
                  </Box>
                </Box>
              )}
            </Stack>
          </CardContent>
        </Card>

        {/* Trabajador */}
        {trabajador && (
          <Paper sx={{ p: 2, borderRadius: 2, mb: 2, display: 'flex', alignItems: 'center', gap: 2, bgcolor: '#EEF2FF' }}>
            <Avatar sx={{ bgcolor: '#1E2783', fontWeight: 700 }}>
              {`${trabajador.nombre_trabajador?.[0] || ''}${trabajador.apellido_trabajador?.[0] || ''}`.toUpperCase()}
            </Avatar>
            <Box>
              <Typography variant="body2" fontWeight={700} color="#1E2783">
                {trabajador.nombre_trabajador} {trabajador.apellido_trabajador}
              </Typography>
              <Typography variant="caption" color="text.secondary">Técnico asignado</Typography>
            </Box>
          </Paper>
        )}

        {/* Detalles */}
        {detalles?.length > 0 && (
          <Card sx={{ borderRadius: 3, mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={700} color="#1E2783" gutterBottom>
                Servicios solicitados
              </Typography>
              {detalles.map((d, i) => (
                <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', py: 1, borderBottom: i < detalles.length - 1 ? '1px solid #F1F5F9' : 'none' }}>
                  <Typography variant="body2">{d.nombre_servicio || 'Servicio'}</Typography>
                  <Typography variant="body2" fontWeight={700} color="#1E2783">
                    {d.cantidad || 1} × ${parseFloat(d.precio || 0).toFixed(2)}
                  </Typography>
                </Box>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Timeline */}
        <Card sx={{ borderRadius: 3, mb: 2 }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={700} color="#1E2783" gutterBottom>
              Línea de tiempo
            </Typography>

            {timeline?.length > 0 ? (
              <Box sx={{ pl: 1 }}>
                {timeline.map((t, i) => {
                  const isLast = i === timeline.length - 1;
                  const isCompleted = !isLast;
                  const Icon = isLast ? CurrentIcon : CheckIcon;
                  const iconColor = isLast ? '#F59E0B' : '#10B981';

                  return (
                    <Box key={i} sx={{ display: 'flex', gap: 2, pb: isLast ? 0 : 2, position: 'relative' }}>
                      {!isLast && (
                        <Box sx={{ position: 'absolute', left: 11, top: 28, bottom: 0, width: 2, bgcolor: '#E2E8F0' }} />
                      )}
                      <Icon sx={{ color: iconColor, fontSize: 24, zIndex: 1, bgcolor: '#fff' }} />
                      <Box>
                        <Typography variant="body2" fontWeight={700}>{t.descripcion_estado}</Typography>
                        <Typography variant="caption" color="text.secondary">{formatDate(t.fecha_real)}</Typography>
                        {t.glosa && (
                          <Typography variant="caption" display="block" color="text.secondary" sx={{ fontStyle: 'italic', mt: 0.5 }}>
                            {t.glosa}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            ) : (
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <CurrentIcon sx={{ color: '#F59E0B' }} />
                <Box>
                  <Typography variant="body2" fontWeight={700}>{orden?.descripcion_estado || 'En proceso'}</Typography>
                  <Typography variant="caption" color="text.secondary">{formatDate(orden?.fecha_orden)}</Typography>
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Footer */}
        <Typography variant="caption" color="text.disabled" textAlign="center" display="block" sx={{ mt: 2 }}>
          Este enlace es válido hasta {formatDate(link_expira)}
        </Typography>
        <Typography variant="caption" color="text.disabled" textAlign="center" display="block" sx={{ mt: 1 }}>
          Powered by <a href="https://trackea.lat" style={{ color: '#1E2783', textDecoration: 'none', fontWeight: 600 }}>TRACKEA</a>
        </Typography>
      </Container>
    </Box>
  );
}
